import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import { IMAGES } from "../../data/site";

const LINES = [
  [{ t: "Where the" }],
  [{ t: "night wears" }],
  [{ t: "gold.", gold: true }],
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ onReserve, scrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.16]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden bg-[var(--br-black)]" data-testid="hero">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={IMAGES.lounge} alt="Inside Black Rose Lounge" className="h-full w-full object-cover object-center" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--br-black)] via-transparent to-[var(--br-black)]/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/25 to-transparent" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 min-h-screen flex flex-col justify-center pt-20">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="overline mb-5 flex items-center gap-3"
        >
          <span className="inline-block h-px w-10 bg-[var(--br-gold)]" />
          Falls Church, Virginia · Est. Nightfall
        </motion.p>

        <h1 className="font-display font-light leading-[1.15] text-[var(--br-cream)] text-5xl sm:text-6xl lg:text-7xl">
          {LINES.map((segments, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block pt-[0.05em] pb-[0.22em]"
                variants={lineVariants}
                custom={i}
                initial="hidden"
                animate="show"
              >
                {segments.map((s, j) =>
                  s.gold ? (
                    <span key={j} className="italic text-[var(--br-gold)]">{s.t}</span>
                  ) : (
                    <span key={j}>{s.t}</span>
                  )
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-6 max-w-md text-[var(--br-cream)]/70 text-base leading-relaxed"
        >
          Black Rose is where authentic Ethiopian culture, blending with American flavors, delicious food, premium drinks, and slow-burning hookah create an unforgettable experience beneath a warm golden glow.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-reserve-btn"
            onClick={onReserve}
            className="gold-btn rounded-full px-9 py-4 text-sm font-semibold tracking-wide"
          >
            Reserve a Table
          </button>
          <button
            data-testid="hero-menu-btn"
            onClick={() => scrollTo("menu")}
            className="rounded-full border border-[var(--br-gold)] px-9 py-4 text-sm font-semibold tracking-wide text-[var(--br-gold)] hover:bg-[var(--br-gold)] hover:text-black transition-all duration-300"
          >
            View Menu
          </button>
          <button
            data-testid="hero-explore-btn"
            onClick={() => scrollTo("experience")}
            className="group flex items-center gap-3 text-sm tracking-wide text-[var(--br-cream)]"
          >
            <span className="grid h-11 w-11 place-items-center rounded-full border border-[var(--br-gold)]/40 group-hover:bg-[var(--br-gold)] group-hover:text-black transition-all duration-300">
              <ArrowDown size={16} />
            </span>
            Explore the lounge
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden lg:flex items-center gap-2 text-xs tracking-widest text-[var(--br-muted)]"
      >
        <MapPin size={14} className="text-[var(--br-gold)]" />
        5815 SEMINARY RD
      </motion.div>
    </section>
  );
};
